import { ApiRef, createApiRef } from '@backstage/core-plugin-api';

/**
 * The result for one end of life cycle.
 *
 * @public
 */
export type EndOfLifeCycle = {
  product: string;
  cycle: string | number;
  releaseDate: string;
  lts: boolean | string | null;
  support: boolean | string | null;
  eol: boolean | string | null;
  extendedSupport: boolean | string | null;
  latest: string | null;
  latestReleaseDate: string | null;
  link: string | null;
  discontinued: boolean | null;
};

export type EolEntry = {
  dependencyName: string;
  version: string;
  'eol-name': string;
  'eol-version': string;
  name?: string;
  codename?: string;
  label?: string;
  releaseDate: string;
  isLts?: boolean;
  ltsFrom?: string;
  isEoas?: boolean;
  eoasFrom?: string;
  isEol?: boolean;
  eolFrom?: string;
  isDiscontinued?: boolean;
  discontinuedFrom?: string;
  isEoes?: boolean;
  eoesFrom?: string;
  isEos?: boolean;
  eosFrom?: string;
  isMaintained?: boolean;
  latest?: {
    name?: string;
    date?: string;
    link?: string;
  };
};

/**
 * Schema for EOL dependencies facts data.
 *
 * This schema defines the structure of EOL dependency information
 * transformed from infrastructure dependency facts with dynamic S3 data.
 *
 * The data field contains an object with a dependencies array of:
 * - name: Original dependency name
 * - version: Original dependency version
 * - eol-name: Converted name for EOL tracking
 * - eol-version: Converted version for EOL tracking
 * - eol: End-of-life date/status (can be date string or boolean)
 * - eoas: End-of-active-support date/status (optional)
 * - eoes: End-of-extended-support date/status (optional)
 * - discontinued: Discontinuation date/status (optional)
 * - releaseDate: Release date (optional)
 * - releaseLabel: Release label/name (optional)
 * - releaseName: Version name from S3 releases data (optional)
 * - date: Alternative release date field (optional)
 */
export type EolFacts = {
  hasAnyEol: boolean; // End of Life
  hasAnyEoas: boolean; // End of Active Support
  hasAnyEoes: boolean; // End of Extended Support
  hasAnyEos: boolean; // End of Support (EOL or Discontinued)
  isAllLts: boolean; // Long Term Support
  isAllMaintained: boolean; // Maintained
  hasAnyDiscontinued: boolean; // Discontinued
  eolData: EolEntry[];
};

export type EndOfLifeResponse = {
  products: EndOfLifeCycle[];
  overview: Omit<EolFacts, 'eolData'>;
};

/**
 * The API used by the end of life plugin.
 *
 * @public
 */
export interface EndOfLifeApi {
  /**
   * Get all product.
   *
   * @public
   */
  getProducts(
    kind: string,
    namespace: string,
    name: string,
  ): Promise<EndOfLifeResponse | undefined>;
}

/**
 * The API used by the end of life plugin.
 *
 * @public
 */
export interface EndOfLifeApi {
  /**
   * Get all product.
   *
   * @public
   */
  getProducts(
    kind: string,
    namespace: string,
    name: string,
  ): Promise<EndOfLifeResponse | undefined>;
}

/**
 * ApiRef for the EndOfLifeApi.
 *
 * @public
 */
export const endOfLifeApiRef: ApiRef<EndOfLifeApi> = createApiRef<EndOfLifeApi>(
  {
    id: 'plugin.endoflife.api',
  },
);
